// cus-review.service.ts
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, tap, shareReplay } from 'rxjs/operators';
import { ApiService } from '../../../../core/services/api/api.service';

export interface VendorStoreExperienceRating {
  ratingId: string;
  purchaseId: string;
  experienceRating: number;
  notes: string | null;
  ratedAt: string;
  status: string;
  user: {
    firstName: string;
    lastName: string;
  };
}

export interface GoogleReviewTrackPayload {
  purchaseId: string;
  reviewId: string;
  source: string;
  action: 'clicked_yes' | 'clicked_no' | 'dismissed';
  ratingAtPrompt: number;
}

export interface VendorStoreExperienceRatingsResponse {
  success: boolean;
  message: string;
  data: {
    vendorId: string;
    ratings: VendorStoreExperienceRating[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  };
}

export interface VendorStoreExperienceSummaryResponse {
  success: boolean;
  message: string;
  data: {
    vendorId: string;
    vendorName: string;
    totalRatings: number;
    avgRating: number;
    distribution: {
      star1: number;
      star2: number;
      star3: number;
      star4: number;
      star5: number;
    };
    last30Days: {
      avgRating: number;
      totalRatings: number;
    };
    lastComputedAt: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class CusReviewService {
  private baseEndpoint = '/shopasst/api/customer/reviews';
  private reviewEndpoint = '/shopasst/api/reviews';

  // Cache for dashboard/balance data
  private reviewWalletCache$: Observable<any> | null = null;
  private reviewWalletCacheTimestamp: number = 0;

  private creditWalletCache$: Observable<any> | null = null;
  private creditWalletCacheTimestamp: number = 0;

  private categoriesCache$: Observable<any> | null = null;
  private categoriesCacheTimestamp: number = 0;

  private readonly CACHE_DURATION = 30000; // 30 seconds

  constructor(
    private http: HttpClient,
    private apiService: ApiService
  ) { }

  // ========== WALLET METHODS ==========

  /**
   * Get review wallet balance (credits earned from reviews)
   */
  // getReviewWalletBalance(): Observable<any> {
  //   const now = Date.now();
  //   const url = this.apiService.getApiUrl(`${this.baseEndpoint}/wallet/review-balance`);

  //   // Return cached data if valid
  //   if (this.reviewWalletCache$ &&
  //     (now - this.reviewWalletCacheTimestamp) < this.CACHE_DURATION) {
  //     return this.reviewWalletCache$;
  //   }

  //   const request$ = this.http.get<any>(url, { withCredentials: true }).pipe(
  //     tap(response => {
  //       if (!response.success) {
  //         console.warn('Review wallet balance fetch warning:', response.message);
  //       }
  //     }),
  //     shareReplay(1),
  //     catchError(error => {
  //       console.error('Error fetching review wallet balance:', error);
  //       this.reviewWalletCache$ = null;
  //       this.reviewWalletCacheTimestamp = 0;
  //       return throwError(() => this.getErrorMessage(error));
  //     })
  //   );

  //   this.reviewWalletCache$ = request$;
  //   this.reviewWalletCacheTimestamp = now;

  //   return request$;
  // }
  getReviewWalletBalance(): Observable<any> {
    const now = Date.now();
    const url = this.apiService.getApiUrl(`${this.baseEndpoint}/wallet/review-balance`);

    if (this.reviewWalletCache$ && (now - this.reviewWalletCacheTimestamp) < this.CACHE_DURATION) {
      return this.reviewWalletCache$;
    }

    const request$ = this.http.get<any>(url, { withCredentials: true }).pipe(
      shareReplay(1),
      catchError(error => {
        this.reviewWalletCache$ = null;
        return throwError(() => this.getErrorMessage(error));
      })
    );

    this.reviewWalletCache$ = request$;
    this.reviewWalletCacheTimestamp = now;
    return request$;
  }

  /**
   * Get credit wallet balance (available credits)
   */
  getCreditWalletBalance(): Observable<any> {
    const now = Date.now();
    const url = this.apiService.getApiUrl(`${this.baseEndpoint}/wallet/credit-balance`);

    // Return cached data if valid
    if (this.creditWalletCache$ &&
      (now - this.creditWalletCacheTimestamp) < this.CACHE_DURATION) {
      return this.creditWalletCache$;
    }

    const request$ = this.http.get<any>(url, { withCredentials: true }).pipe(
      tap(response => {
        if (!response.success) {
          console.warn('Credit wallet balance fetch warning:', response.message);
        }
      }),
      shareReplay(1),
      catchError(error => {
        console.error('Error fetching credit wallet balance:', error);
        this.creditWalletCache$ = null;
        this.creditWalletCacheTimestamp = 0;
        return throwError(() => this.getErrorMessage(error));
      })
    );

    this.creditWalletCache$ = request$;
    this.creditWalletCacheTimestamp = now;

    return request$;
  }

  /**
   * Get available credits (alias for credit wallet balance)
   */
  // getAvailableCredits(): Observable<any> {
  //   const now = Date.now();
  //   const url = this.apiService.getApiUrl(`${this.baseEndpoint}/available-credits`);

  //   // Return cached data if valid
  //   if (this.creditWalletCache$ &&
  //     (now - this.creditWalletCacheTimestamp) < this.CACHE_DURATION) {
  //     return this.creditWalletCache$;
  //   }

  //   const request$ = this.http.get<any>(url, { withCredentials: true }).pipe(
  //     tap(response => {
  //       if (!response.success) {
  //         console.warn('Available credits fetch warning:', response.message);
  //       }
  //     }),
  //     shareReplay(1),
  //     catchError(error => {
  //       console.error('Error fetching available credits:', error);
  //       this.creditWalletCache$ = null;
  //       this.creditWalletCacheTimestamp = 0;
  //       return throwError(() => this.getErrorMessage(error));
  //     })
  //   );

  //   this.creditWalletCache$ = request$;
  //   this.creditWalletCacheTimestamp = now;

  //   return request$;
  // }

  // ========== CATEGORY METHODS ==========

  /**
   * Get categories for review dropdown
   */
  getReviewCategories(): Observable<any> {
    const now = Date.now();
    const url = this.apiService.getApiUrl(`${this.baseEndpoint}/categories`);

    // Return cached data if valid
    if (this.categoriesCache$ &&
      (now - this.categoriesCacheTimestamp) < this.CACHE_DURATION) {
      return this.categoriesCache$;
    }

    const request$ = this.http.get<any>(url, { withCredentials: true }).pipe(
      shareReplay(1),
      catchError(error => {
        console.error('Error fetching review categories:', error);
        this.categoriesCache$ = null;
        this.categoriesCacheTimestamp = 0;
        return throwError(() => this.getErrorMessage(error));
      })
    );

    this.categoriesCache$ = request$;
    this.categoriesCacheTimestamp = now;

    return request$;
  }

  /**
   * Get eligible vendors for a category
   * Returns vendors in format: "Category from Shop (Date)"
   */
  getEligibleVendors(categoryId: string): Observable<any> {
    const url = this.apiService.getApiUrl(`${this.baseEndpoint}/eligible-vendors/${categoryId}`);

    return this.http.get<any>(url, { withCredentials: true }).pipe(
      catchError(error => {
        console.error('Error fetching eligible vendors:', error);
        return throwError(() => this.getErrorMessage(error));
      })
    );
  }

  // ========== REVIEW CRUD METHODS ==========

  /**
   * Submit a review with optional images
   */
  // submitReview(formData: FormData): Observable<any> {
  //   const url = this.apiService.getApiUrl(this.baseEndpoint);
  //   console.log(formData);
    

  //   return this.http.post<any>(url, formData, { withCredentials: true }).pipe(
  //     tap(response => {
  //       if (response.success) {
  //         console.log('Review submitted successfully:', response);
  //         // Clear caches on successful submission
  //         this.clearCaches();
  //       }
  //     }),
  //     catchError(error => {
  //       console.error('Error submitting review:', error);
  //       return throwError(() => this.getErrorMessage(error));
  //     })
  //   );
  // }
  // submitReview(purchaseId: string, body: { rating: number; reviewText: string }): Observable<any> {
  //   const url = this.apiService.getApiUrl(`${this.baseEndpoint}/purchase/${purchaseId}`);

  //   return this.http.post<any>(url, body, { withCredentials: true }).pipe(
  //     tap(response => {
  //       if (response.success) {
  //         this.clearCaches();
  //       }
  //     }),
  //     catchError(error => {
  //       console.error('Error submitting review:', error);
  //       return throwError(() => this.getErrorMessage(error));
  //     })
  //   );
  // }

  /**
   * Rate store experience for a completed purchase
   */
  rateStoreExperience(purchaseId: string, experienceRating: number): Observable<any> {
    const url = this.apiService.getApiUrl('/shopasst/api/store-experience/rate');

    return this.http.post<any>(
      url,
      { purchaseId, experienceRating },
      { withCredentials: true }
    ).pipe(
      catchError(error => {
        console.error('Error rating store experience:', error);
        return throwError(() => this.getErrorMessage(error));
      })
    );
  }

  /**
   * Get store experience ratings for a vendor
   */
  getVendorStoreExperienceRatings(vendorId: string): Observable<VendorStoreExperienceRatingsResponse> {
    const url = this.apiService.getApiUrl(`/shopasst/api/store-experience/vendor/${vendorId}/ratings`);

    return this.http.get<VendorStoreExperienceRatingsResponse>(url, { withCredentials: true }).pipe(
      catchError(error => {
        console.error('Error fetching store experience ratings:', error);
        return throwError(() => this.getErrorMessage(error));
      })
    );
  }

  getVendorStoreExperienceSummary(vendorId: string): Observable<VendorStoreExperienceSummaryResponse> {
    const url = this.apiService.getApiUrl(`/shopasst/api/store-experience/vendor/${vendorId}/summary`);

    return this.http.get<VendorStoreExperienceSummaryResponse>(url, { withCredentials: true }).pipe(
      catchError(error => {
        console.error('Error fetching store experience summary:', error);
        return throwError(() => this.getErrorMessage(error));
      })
    );
  }

  submitReview(
      purchaseId: string,
      body: { rating: number; reviewText: string },
      photos: File[] = []
    ): Observable<any> {
    const url = this.apiService.getApiUrl(`${this.baseEndpoint}/purchase/${purchaseId}`);

    // Build FormData so images are sent as multipart/form-data
    const formData = new FormData();
    formData.append('rating', body.rating.toString());
    formData.append('reviewText', body.reviewText);

    // Append each photo under the key 'images' (matches -F "images=@...")
    photos.forEach(photo => {
      formData.append('images', photo, photo.name);
    });

    console.log('FormData entries:');
    formData.forEach((value, key) => {
      console.log(key, value instanceof File ? `File: ${(value as File).name}` : value);
    });

    return this.http.post<any>(url, formData, { withCredentials: true }).pipe(
      tap(response => {
        if (response.success) {
          this.clearCaches();
        }
      }),
      catchError(error => {
        console.error('Error submitting review:', error);
        return throwError(() => this.getErrorMessage(error));
      })
    );
  }
  // submitReview(
  //   purchaseId: string,
  //   body: { rating: number; reviewText: string },
  //   photos: File[] = [],
  //   videos: File[] = []
  // ): Observable<any> {
  //   const url = this.apiService.getApiUrl(`/shopasst/api/reviews`);

  //   const formData = new FormData();

  //   // required body fields
  //   formData.append('purchaseId', purchaseId);
    
  //   formData.append('rating', body.rating.toString());
  //   formData.append('reviewText', body.reviewText);

  //   // images
  //   photos.forEach(photo => {
  //     formData.append('images', photo, photo.name);
  //   });

  //   // videos
  //   videos.forEach(video => {
  //     formData.append('videos', video, video.name);
  //   });

  //   console.log('FormData entries:');
  //   formData.forEach((value, key) => {
  //     console.log(
  //       key,
  //       value instanceof File ? `File: ${value.name}` : value
  //     );
  //   });

  //   return this.http.post<any>(url, formData, {
  //     withCredentials: true
  //   }).pipe(
  //     tap(res => {
  //       if (res.success) {
  //         this.clearCaches();
  //       }
  //     }),
  //     catchError(error => {
  //       console.error('Error submitting review:', error);
  //       return throwError(() => error);
  //     })
  //   );
  // }

  /**
   * Get my reviews with pagination
   */
  getMyReviews(page: number = 1, limit: number = 10): Observable<any> {
    const url = this.apiService.getApiUrl(`${this.baseEndpoint}/my-reviews`);

    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    return this.http.get<any>(url, { params, withCredentials: true }).pipe(
      catchError(error => {
        console.error('Error fetching my reviews:', error);
        return throwError(() => this.getErrorMessage(error));
      })
    );
  }

  /**
   * Get reviews from recent purchases
   */
  getPurchaseReviews(
    categoryId?: number,
    page: number = 1,
    limit: number = 10
  ): Observable<any> {
    const url = this.apiService.getApiUrl(`${this.baseEndpoint}/recent-purchases`);

    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    if (categoryId !== undefined && categoryId !== null) {
      params = params.set('categoryId', categoryId);
    }

    return this.http.get<any>(url, { params, withCredentials: true }).pipe(
      catchError(error => {
        console.error('Error fetching purchase reviews:', error);
        return throwError(() => this.getErrorMessage(error));
      })
    );
  }

  // ========== VOTE METHODS ==========

  /**
   * Vote on a review (helpful/not helpful)
   */
  voteReview(reviewId: string, voteType: 'helpful' | 'not_helpful'): Observable<any> {
    const url = this.apiService.getApiUrl(`${this.baseEndpoint}/${reviewId}/vote`);

    return this.http.post<any>(url, { voteType }, { withCredentials: true }).pipe(
      catchError(error => {
        console.error('Error voting on review:', error);
        return throwError(() => this.getErrorMessage(error));
      })
    );
  }

  /**
   * Get my vote on a review
   */
  getMyVote(reviewId: string): Observable<any> {
    const url = this.apiService.getApiUrl(`${this.baseEndpoint}/${reviewId}/my-vote`);

    return this.http.get<any>(url, { withCredentials: true }).pipe(
      catchError(error => {
        console.error('Error fetching my vote:', error);
        return throwError(() => this.getErrorMessage(error));
      })
    );
  }

  // ========== COMMENT METHODS ==========

  /**
   * Add comment to review
   */
  addComment(reviewId: string, commentText: string): Observable<any> {
    const url = this.apiService.getApiUrl(`${this.baseEndpoint}/${reviewId}/comment`);

    return this.http.post<any>(url, { commentText }, { withCredentials: true }).pipe(
      catchError(error => {
        console.error('Error adding comment:', error);
        return throwError(() => this.getErrorMessage(error));
      })
    );
  }

  /**
   * Get comments for a review
   */
  getComments(
    reviewId: string,
    page: number = 1,
    limit: number = 20
  ): Observable<any> {
    const url = this.apiService.getApiUrl(`${this.baseEndpoint}/${reviewId}/comments`);

    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    return this.http.get<any>(url, { params, withCredentials: true }).pipe(
      catchError(error => {
        console.error('Error fetching comments:', error);
        return throwError(() => this.getErrorMessage(error));
      })
    );
  }

  /**
   * Update own comment
   */
  updateComment(
    reviewId: string,
    commentId: number,
    commentText: string
  ): Observable<any> {
    const url = this.apiService.getApiUrl(`${this.baseEndpoint}/${reviewId}/comments/${commentId}`);

    return this.http.put<any>(url, { commentText }, { withCredentials: true }).pipe(
      catchError(error => {
        console.error('Error updating comment:', error);
        return throwError(() => this.getErrorMessage(error));
      })
    );
  }

  /**
   * Delete own comment
   */
  deleteComment(
    reviewId: string,
    commentId: number
  ): Observable<any> {
    const url = this.apiService.getApiUrl(`${this.baseEndpoint}/${reviewId}/comments/${commentId}`);

    return this.http.delete<any>(url, { withCredentials: true }).pipe(
      catchError(error => {
        console.error('Error deleting comment:', error);
        return throwError(() => this.getErrorMessage(error));
      })
    );
  }

  // ========== UTILITY METHODS ==========

  getPurchaseById(purchaseId: string): Observable<any> {
    const url = this.apiService.getApiUrl(
      `/shopasst/api/customer/my-purchases/${purchaseId}`
    );
    return this.http.get<any>(url, { withCredentials: true }).pipe(
      catchError(error => throwError(() => this.getErrorMessage(error)))
    );
  }
  
  // ========== To check voice feedback status ==========
  getVoiceFeedbackStatus(purchaseId: string): Observable<any> {
    const url = this.apiService.getApiUrl(
      `/shopasst/api/customer/voice-feedback/purchase/${purchaseId}/status`
    );
    return this.http.get<any>(url, { withCredentials: true }).pipe(
      catchError(error => throwError(() => this.getErrorMessage(error)))
    );
  }

  getReviewById(reviewId: string): Observable<any> {
    const url = this.apiService.getApiUrl(`${this.reviewEndpoint}/${reviewId}`);
    return this.http.get<any>(url);
  }


  /**
 * Track user's action on the post-review Google Review prompt.
 * Non-critical — errors are swallowed so a failed tracking call
 * never blocks the user's flow (e.g. redirecting to Google Reviews).
 */
  trackGoogleReviewAction(payload: GoogleReviewTrackPayload): Observable<any> {
    const url = this.apiService.getApiUrl('/shopasst/api/google-review/track');

    return this.http.post<any>(url, payload, { withCredentials: true }).pipe(
      catchError(error => {
        console.error('Error tracking google review action (non-blocking):', error);
        return of(null);
      })
    );
  }

  /**
   * Clear all caches
   */
  private clearCaches(): void {
    this.reviewWalletCache$ = null;
    this.reviewWalletCacheTimestamp = 0;
    this.creditWalletCache$ = null;
    this.creditWalletCacheTimestamp = 0;
    this.categoriesCache$ = null;
    this.categoriesCacheTimestamp = 0;
  }

  /**
   * Helper method to extract error message from API response
   */
  private getErrorMessage(error: any): string {
    if (error.error?.message) {
      return error.error.message;
    } else if (error.status === 403) {
      return 'Session expired or invalid. Please login again.';
    } else if (error.status === 401) {
      return 'Unauthorized. Please login again.';
    } else if (error.status === 404) {
      return 'Resource not found.';
    } else if (error.status === 400) {
      return error.error?.message || 'Validation failed. Please check your input.';
    } else if (error.status === 0) {
      return 'Network error. Please check your connection.';
    } else {
      return 'An unexpected error occurred. Please try again.';
    }
  }
}